import GlobalIcon from "images/global.svg?react";
import DownArrowIcon from "@mui/icons-material/KeyboardArrowDown";
import { HStack } from "components/Stack";
import "./style.css";
import { Popover, PopoverProps } from "@mui/material";
import { MouseEvent, useState } from "react";
import { useLocalization } from "contexts/LocalizationContext";
import classNames from "classnames";
import { UiLanguage, UI_LANGUAGES } from "services/i18n";
import { activateOnKeys } from "utils/a11y";

type Props = {
  className?: string;
  color?: string;
};

type LanguageDropdownProps = {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onSelect: (value: UiLanguage) => void;
  transformOrigin?: PopoverProps["transformOrigin"];
  value: UiLanguage;
};

export function LanguageDropdown(props: LanguageDropdownProps) {
  const hdSelect = (value: UiLanguage) => {
    if (value !== props.value) {
      props.onSelect(value);
    }
  };

  return (
    <Popover
      anchorEl={props.anchorEl}
      open={Boolean(props.anchorEl)}
      onClose={props.onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      transformOrigin={
        props.transformOrigin ?? {
          vertical: "top",
          horizontal: "left",
        }
      }
      slotProps={{
        paper: {
          className: "page-header-menu language-selector-menu",
        },
      }}
    >
      {UI_LANGUAGES.map(lang => {
        return (
          <div
            key={lang.value}
            className="menu-item"
            role="menuitem"
            tabIndex={0}
            onClick={() => hdSelect(lang.value)}
            onKeyDown={activateOnKeys(() => hdSelect(lang.value))}
          >
            <span className="menu-label">{lang.name}</span>
          </div>
        );
      })}
    </Popover>
  );
}

export default function AppLanguageSwitcher(props: Props) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { language, changeLanguage: setLanguage } = useLocalization();

  const hdOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const hdSelect = (v: UiLanguage) => {
    setLanguage(v);
    setAnchorEl(null);
  };

  return (
    <>
      <div
        onClick={hdOpen}
        role="button"
        tabIndex={0}
        aria-haspopup="menu"
        aria-expanded={Boolean(anchorEl)}
        onKeyDown={evt => {
          if (evt.key === "Enter" || evt.key === " ") {
            evt.preventDefault();
            setAnchorEl(evt.currentTarget);
          }
        }}
        className={classNames(props.className)}
      >
        <HStack
          gap={"6px"}
          align={"center"}
          className={"language-selector"}
          style={{ color: props.color || "white" }}
        >
          <GlobalIcon style={{ color: "inherit" }} />
          <div className={"language"}>{language.toUpperCase()}</div>
          <DownArrowIcon style={{ color: "inherit" }} />
        </HStack>
      </div>

      <LanguageDropdown
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        onSelect={hdSelect}
        value={language}
      />
    </>
  );
}
